import React, { useState, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { PopupPosition } from '../common/PopupPositions';
import { Toast } from 'devextreme-react/toast';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import 'devextreme-react/text-area';

import LabelTemplate from '../label-template/LabelTemplate';

import { ITableSelectionFormHandle, ITableSelectionFormProps } from './ITableSelectionForm';

import { formatMessage } from "devextreme/localization";

import TableApi from "./../../api/TableApi";
import FieldApi from "./../../api/FieldApi";

import { JobStatusError } from '../../classes/consts/JobStatus';

import TableReferenceIdAndKeyDto from '../../classes/dtos/TableReferenceIdAndKeyDto';

import TableSelectionDto from '../../classes/dtos/datastructuremodule/TableSelectionDto';

import TableForm, { TableFormHandle } from '../table-form/TableForm';


const TableSelectionListForm = forwardRef<ITableSelectionFormHandle, ITableSelectionFormProps>(({ OriginTableId, OriginPathId, DataSourceId, DatabaseId, WithCreate, WithRefresh, SetTableSelected }, ref) => {

    const position = { of: '#root' };

    const tableFormHandleRef = useRef<TableFormHandle>(null);

    const [tableSelectionDto, setTableSelectionDto] = useState<TableSelectionDto>(new TableSelectionDto(null));

    const [isOpen, setIsOpen] = useState(false);

    const [onlyUpdatable, setOnlyUpdatable] = useState(false);

    const [refreshInProgress, setRefreshInProgress] = React.useState(false);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success' | 'warning',
        message: string,
        withRedirect: boolean
    });

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

    }, [toastConfig]);

    useImperativeHandle(ref, () => ({
        Open(tableReferenceId: number, onlyUpdatable: boolean) {
            setOnlyUpdatable(onlyUpdatable);
            setIsOpen(true);

            TableApi.GetTablesAsync(DataSourceId, DatabaseId, false)
                .then(getTablesResult => {
                    let allTables = getTablesResult.Result.Tables
                        .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                    if (onlyUpdatable) {
                        allTables = allTables.filter((e) => !e.ReadOnly);
                    }
                    let current = allTables.find((e) => e.Id === tableReferenceId) ?? null;
                    setTables(allTables);
                    setTableSelectionDto(new TableSelectionDto(current));
                });
        }
    }));

    const formRef = useRef<FormRef>(null);

    const [tables, setTables] = useState<TableReferenceIdAndKeyDto[] | null>(null);

    const tableEditorOptions = {
        items: tables,
        searchEnabled: true,
        displayExpr: "Name",
        buttons: [
            "dropDown",
            {
                name: 'edit',
                location: 'after',
                options: {
                    visible: WithRefresh,
                    stylingMode: 'text',
                    icon: 'refresh',
                    onClick: async () => {
                        setRefreshInProgress(true);

                        let getTablesResult = await TableApi.GetTablesAsync(DataSourceId, DatabaseId, true);
                        if (getTablesResult.IsFailed()) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: getTablesResult.ErrorMessage,
                                withRedirect: false
                            });
                            return;
                        }

                        if (getTablesResult.Result.Accepted === false) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: formatMessage('RefreshTablesNotAccepted'),
                                withRedirect: false
                            });
                            return;
                        }

                        if (getTablesResult.Result.Status === JobStatusError) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: formatMessage('RefreshTablesError'),
                                withRedirect: false
                            });
                            return;
                        }

                        let allTables = getTablesResult.Result.Tables
                            .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                        if (onlyUpdatable) {
                            allTables = allTables.filter((e) => !e.ReadOnly);
                        }
                        let current = allTables.find((e) => e.Id === tableSelectionDto.Table?.Id) ?? null;
                        setTables(allTables);
                        setTableSelectionDto(new TableSelectionDto(current));
                        setRefreshInProgress(false);

                        setToastConfig({
                            ...toastConfig,
                            isVisible: true,
                            type: 'success',
                            message: formatMessage('RefreshTablesSuccess'),
                            withRedirect: false
                        });
                    }
                },
            }]
    };

    const validationRules: {
        table: ValidationRule[]
    } = {
        table: [
            { type: 'required', message: 'Table is required.' },
        ]
    };

    function createTable(): void {
        if (tableFormHandleRef.current !== null) {
            tableFormHandleRef.current.Open(DatabaseId, null, OriginTableId, OriginPathId);
        }
    };

    function cancel(): void {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        setRefreshInProgress(true);

        if (tableSelectionDto.Table !== null) {
            let getFieldsResult = await FieldApi.GetRawFieldsAsync(DataSourceId, tableSelectionDto.Table?.Id, false);
            if (getFieldsResult.IsFailed()) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: getFieldsResult.ErrorMessage,
                    withRedirect: false
                });
                return;
            }

            if (getFieldsResult.Result.Accepted === false) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshFieldsNotAccepted'),
                    withRedirect: false
                });
                return;
            }

            if (getFieldsResult.Result.Status === JobStatusError) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshFieldsError'),
                    withRedirect: false
                });
                return;
            }
        }

        SetTableSelected(tableSelectionDto);

        setRefreshInProgress(false);
        setIsOpen(false);
    };

    const createTableButtonOptions = {
        text: 'Create a new table',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        icon: 'plus',
        onClick: createTable
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: cancel
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    const handleTableCreated = async (name: string) => {
        // The table is now created in the source: force a refresh to discover it.
        let getTablesResult = await TableApi.GetTablesAsync(DataSourceId, DatabaseId, true);

        if (getTablesResult.IsFailed()) {
            return;
        }

        if (!getTablesResult.Result.Accepted) {
            return;
        }

        let allTables = [...getTablesResult.Result.Tables].sort((a, b) => a.Name.localeCompare(b.Name, "fr", { sensitivity: "base" }));

        let current = allTables.find((e) => e.Name === name) ?? null;

        setTables(allTables);
        setTableSelectionDto((previous) => { return { ...previous, Table: current }; });
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={cancel}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title="Choose your table"
                container=".dx-viewport"
                width={600}
                height="auto"
                position={PopupPosition}
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        ref={formRef}
                        formData={tableSelectionDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="TableSelectionList"
                        colCount={2}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="Table" editorType="dxSelectBox" editorOptions={tableEditorOptions} validationRules={validationRules.table}>
                                <Label render={LabelTemplate('product')} />
                            </SimpleItem>
                        </GroupItem>
                        {WithCreate && <GroupItem colSpan={2}>
                            <div style={{ width: '100%', height: 36, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'inline-flex' }}>
                                <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                    <div style={{ color: '#9393A2', fontSize: 10, fontFamily: 'Manrope', fontWeight: '600', textTransform: 'uppercase', lineHeight: '13px', letterSpacing: 1, wordWrap: 'break-word' }}>OR</div>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                </div>
                            </div>
                        </GroupItem>}
                        {WithCreate && <GroupItem colSpan={2}>
                            <ButtonItem buttonOptions={createTableButtonOptions} />
                        </GroupItem>}
                        <EmptyItem colSpan={2} />
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={refreshInProgress}
            />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={toastConfig.type === 'success' ? 1000 : 3000}
            />
            <TableForm ref={tableFormHandleRef} TableAdded={handleTableCreated} />
        </React.Fragment>
    );
});

export default TableSelectionListForm;